import { apiClient } from '../api/client'
import type { components } from '../api/schema.gen'
import type { RfqBundleItem } from './rfq-bundle-context'

/** The two OPTIONAL, server-derived pricing fields a full `Sourceable` carries
 * and a persisted `SourceableRef` does not — see `RfqBundleItem['sourceable']`.
 * This module exists to put exactly these two back, and nothing else. */
interface ResolvedPricing {
  readonly indicativePrice?: components['schemas']['Price']
  readonly pricingProjection?: components['schemas']['PricingProjection']
}

/**
 * UID-464 (ADR-0151) — THE named, reusable rehydrate-time price re-resolution
 * seam, and the second half of ADR-0103 D5.
 *
 * **The defect this closes.** D5 rules that server-derived money must never be
 * persisted, and states the obligation that makes that safe: *"Prices must
 * re-resolve from the server on rehydrate."* Only the first half was ever
 * built. `sanitizeItemForPersist` strips `indicativePrice`/`pricingProjection`
 * on the way into `sessionStorage`, the rehydrate puts back a bare
 * `SourceableRef`, and `RfqReviewPage` derives its lines straight from those
 * items (the bundle IS the compose-time source of truth, ADR-0082 D1) — so a
 * refresh silently blanked the Unit price cell, the auto-priced preview range,
 * the "Volume" quantity-break link, the Checkout CTA, and the whole pre-quote
 * pricing preview. Nothing re-resolved, because nothing existed to.
 *
 * **Why a named function and not logic inside the provider's effect.** The
 * `ensure-lines-on-server-draft.ts` posture (ADR-0117 D11): a compose-time
 * reconcile step is a NAMED, REUSABLE function so the next surface that needs
 * it calls this instead of re-deriving it at a second call site. Keeping it out
 * of the component also makes every branch below unit-testable without
 * mounting React.
 *
 * **The caller decides WHICH items to pass.** This resolves every item handed
 * to it and does not sniff for "does this already have a price?" — it cannot,
 * soundly: a `quote_required` Sourceable (DFM fail, UID-49) legitimately
 * carries no `indicativePrice`, so "no price" is indistinguishable from
 * "sanitized". `RfqBundleProvider` passes exactly the snapshot's items, which
 * are sanitized by construction, and that is the whole guard.
 *
 * **Never throws.** Every read is settled independently; an item whose read
 * failed (error body OR rejected promise — offline, DNS, abort) is returned
 * REFERENTIALLY IDENTICAL to its input, which is both the honest outcome (the
 * pre-fix behaviour: no price rather than a wrong one) and what lets React skip
 * re-rendering it. A price fix must never be able to break compose.
 */
/**
 * The SKU-match window on the substring catalog search. Mirrors
 * `SKU_SEARCH_LIMIT` in `apps/api/src/modules/catalog/catalog-sku-price-lookup.ts`
 * and exists for the reason that module states: `search` matches SUBSTRINGS, so
 * an exact SKU can be pushed off the first page by its own substring siblings
 * (`HOSBS19N` also matches `HOSBS19NA`, …). Generous enough that the exact row
 * is always on the page, bounded so a short SKU cannot pull the whole catalog.
 */
const SKU_SEARCH_LIMIT = 100

/** One read's outcome. `absent` is the ONLY one that falls through to the other
 * read: `found` is an answer (even a priceless one — a `quote_required` part),
 * and `error` is absence of KNOWLEDGE, where guessing risks a wrong price. */
type ReadOutcome =
  | { readonly status: 'found'; readonly pricing: ResolvedPricing }
  | { readonly status: 'absent' }
  | { readonly status: 'error' }

/** `GET /parts/{partId}` — authoritative and exact: a tenant library part's id
 * IS its `parts` row key, so an id match cannot be the wrong part. */
async function readLibraryById(item: RfqBundleItem): Promise<ReadOutcome> {
  const partId = item.sourceable.id
  try {
    const result = await apiClient.GET('/parts/{partId}', { params: { path: { partId } } })
    const sourceable = result.data?.sourceable
    if (sourceable && sourceable.id === partId) {
      return { status: 'found', pricing: pricingOf(sourceable) }
    }
    const status = (result.error as { readonly status?: number } | undefined)?.status
    // 404 is a FACT — "this id is not a library part" — and is exactly what
    // licenses trying the catalog instead. A 5xx is not knowledge.
    return status === 404 ? { status: 'absent' } : { status: 'error' }
  } catch {
    return { status: 'error' }
  }
}

/** `GET /catalog/search?q=<sku>` reduced to a point read by an EXACT,
 * case-insensitive `catalogSku` match — the same shape
 * `catalog-sku-price-lookup.ts`'s `findCatalogRow` uses, and for the same
 * stated reason: pricing off the first hit "would charge a sibling SKU's price
 * for the SKU the buyer actually ordered". */
async function readCatalogBySku(
  item: RfqBundleItem,
  searchCache: Map<string, Promise<readonly { readonly sourceable: unknown }[]>>,
): Promise<ReadOutcome> {
  const sku = item.sku?.trim()
  // No SKU is nothing to search on — see ADR-0151 D3's recorded gap.
  if (!sku) return { status: 'absent' }
  try {
    let rows = searchCache.get(sku)
    if (!rows) {
      rows = apiClient
        .GET('/catalog/search', { params: { query: { q: sku, limit: SKU_SEARCH_LIMIT } } })
        .then((result) => {
          if (!result.data) throw new Error('catalog search failed')
          return result.data.items
        })
      searchCache.set(sku, rows)
    }
    const wanted = sku.toLowerCase()
    for (const row of await rows) {
      const sourceable = row.sourceable as {
        readonly catalogSku?: string | null
        readonly indicativePrice?: components['schemas']['Price'] | null
        readonly pricingProjection?: components['schemas']['PricingProjection']
      }
      // Matched on `catalogSku`, EXACTLY and case-insensitively — never on
      // `sourceable.id`, which `catalogItemToSourceable` mints fresh with
      // `randomUUID()` on every request (UID-32), and never on "the search
      // returned something, take row 0".
      if (sourceable.catalogSku?.toLowerCase() === wanted) {
        return { status: 'found', pricing: pricingOf(sourceable) }
      }
    }
    return { status: 'absent' }
  } catch {
    return { status: 'error' }
  }
}

export async function resolveBundleItemPrices(
  items: readonly RfqBundleItem[],
): Promise<readonly RfqBundleItem[]> {
  if (items.length === 0) return items

  // One in-flight search per distinct SKU, shared by every item that needs it.
  const searchCache = new Map<string, Promise<readonly { readonly sourceable: unknown }[]>>()

  const resolutions = await Promise.all(
    items.map(async (item) => {
      // BOTH reads, always, ordered so the common path for this `kind` costs
      // ONE call — mirroring `catalog-sku-price-lookup.ts`'s `findItem`, and
      // for the reason it states outright: the answer must never depend on the
      // client's `kind` claim being right, because **`kind` is a hint, not
      // provenance**. Two shipped defects prove it in both directions:
      //   - a CATALOG-SOURCED `configurable` part (the Add-parts modal returns
      //     them, and `toReviewLine` stamps an accountId on every non-catalog
      //     kind) is not in the tenant library ⇒ the point read 404s;
      //   - a TENANT-LIBRARY part of `kind: 'catalog'` — a "buy" line in a
      //     customer BOM, e.g. `MSM-608ZZ`
      //     (`tenant-library-repository.integration.test.ts:275-277`) — is not
      //     in the catalog service ⇒ the SKU search misses.
      // Inferring provenance from `kind` gets one of those wrong whichever way
      // you point it. Discovering it costs at most one extra read.
      const reads =
        item.sourceable.kind === 'catalog'
          ? [() => readCatalogBySku(item, searchCache), () => readLibraryById(item)]
          : [() => readLibraryById(item), () => readCatalogBySku(item, searchCache)]

      for (const read of reads) {
        const outcome = await read()
        if (outcome.status === 'found') return [item.sourceable.id, outcome.pricing] as const
        if (outcome.status === 'error') return null // do not guess with the other read
      }
      return null
    }),
  )

  const pricingBySourceableId = new Map<string, ResolvedPricing>()
  for (const resolution of resolutions) {
    if (resolution) pricingBySourceableId.set(resolution[0], resolution[1])
  }
  if (pricingBySourceableId.size === 0) return items

  return items.map((item) => {
    const pricing = pricingBySourceableId.get(item.sourceable.id)
    // Nothing resolved for this item — hand back the SAME object, not a copy.
    if (pricing === undefined) return item
    if (pricing.indicativePrice === undefined && pricing.pricingProjection === undefined) {
      // Found, and genuinely unpriced (a `quote_required` / DFM-fail
      // Sourceable). Spreading empties would allocate for no change.
      return item
    }
    return {
      ...item,
      // ADR-0151 D2 — merges ONLY the two pricing fields onto the PERSISTED
      // sourceable; never swaps in the server's whole copy. The persisted ref
      // carries the SESSION-stamped `accountId` (`toReviewLine`: the server
      // 403s a foreign accountId on append/cart writes) and the `uom` every
      // append and cart write reads. It matters doubly for a catalog row,
      // whose freshly-read `id` is a per-request uuid that would corrupt the
      // line's identity if adopted.
      sourceable: { ...item.sourceable, ...pricing },
    }
  })
}

/** Narrows a freshly-read `Sourceable` to just the two fields this seam is
 * allowed to propagate, dropping absent ones so a `{indicativePrice:
 * undefined}` key can never overwrite a present value on the merge above. */
function pricingOf(sourceable: {
  readonly indicativePrice?: components['schemas']['Price'] | null
  readonly pricingProjection?: components['schemas']['PricingProjection']
}): ResolvedPricing {
  return {
    ...(sourceable.indicativePrice ? { indicativePrice: sourceable.indicativePrice } : {}),
    ...(sourceable.pricingProjection ? { pricingProjection: sourceable.pricingProjection } : {}),
  }
}
